import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useAccess from "@hooks/useAccess";
import { ApplicationContext } from "@context/ApplicationContext";
import { AuthContext } from "@context/AuthContext";
import { BookSessionContext } from "@context/BookSessionContext";
import { PromoContext } from "@context/PromoContext";
import { SessionContext } from "@context/SessionContext";
import { UserContext } from "@context/UserContext";
import CloseIcon from "@components/Icons/CloseIcon";
import RedemptionDetailsBase from "@components/RedemptionDetails/RedemptionDetailsBase";
import Loader from "@components/Ui/Loader";
import PaymentStatus from "@components/V3/PaymentStatus";
import StripeCheckoutDrawer from "@components/V3/StripeCheckoutDrawer";
import TermsDrawer from "@components/V3/TermsDrawer";
import BookingHeartbeat from "@components/V3/V3BookSession/bookingHeartbeat";
import BookSessionDetails from "@components/V3/V3BookSession/BookSessionDetails";
import BookSessionInventory from "@components/V3/V3BookSession/BookSessionInventory";
import V3ComplimentaryCheckoutDrawer from "@components/V3/V3ComplimentaryCheckoutDrawer";

import "./style.scss";

const V2V3BookSession = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const { token, user, isLoggedIn, isReadytoCheckout, drawerState } = useContext(AuthContext);
    const { whiteLabel } = useContext(ApplicationContext);
    const {
        bookingSessionId,
        uuid,
        setBookingSessionId,
        setCheckout,
        setTicketQuantity,
        selectedItems,
        setSelectedItems,
        setPromoCodeSuccess,
        setError,
        checkoutBookingSession,
        setPromoCodeMessage,
        isSubmitting,
        isConfirmingPayment,
        referralCode,
        setReferralCode,
        setReferralSuccess,
        setTermsAccepted,
        setProceedToCheckout
    } = useContext(BookSessionContext);

    const {
        setIsSubscriberPromo,
        fetchApplyPromoAvailability,
        appliedPromo,
        setAppliedPromo,
        handleSelectPromo,
        setStandardPromoCode,
    } = useContext(PromoContext);

    const {
        session,
        setSession,
        fetchPurchasable,
        isLoadingSession,
        purchasable,
    } = useContext(SessionContext);

    const {
        fetchBookedTicketForSession,
        setBookedTicketForSession,
    } = useContext(UserContext);

    const { isFullyLoggedIn } = useAccess();

    const [showCheckout, setShowCheckout] = useState(false);
    const [hasFastLoggedIn, setHasFastLoggedIn] = useState(false);
    const [hasFetchedPromo, setHasFetchedPromo] = useState(false);

    const totalTicketQuantity = selectedItems.reduce((acc, cur) => {
        return cur.type === "ticket" ? acc + cur.quantity : acc;
    }, 0);

    // Resets all promo information to default (null)
    useEffect(() => {
        setPromoCodeSuccess(null);
        setReferralSuccess(null);
        setPromoCodeMessage("");

        setStandardPromoCode("");
        setIsSubscriberPromo(false);
        setAppliedPromo(null);
        handleSelectPromo(null);
    }, []);

    // Fetches available purchase items for the session, and refreshes selected items states
    useEffect(() => {
        fetchPurchasable(id);
        if (token) {
            fetchBookedTicketForSession(id);
        }
        return () => {
            setBookingSessionId(null);
            setSession(null);
            setError(null);
            setSelectedItems([]);
            setBookedTicketForSession([]);
            setTermsAccepted(false);
            setProceedToCheckout(false);
        };
    }, [id]);

    useEffect(() => {
        if (user && user?.id) {
            fetchApplyPromoAvailability();
        }
    }, [user?.id]);

    // Applies the promo to the purchase
    useEffect(() => {
        handleApplyPromo();
    }, [session, appliedPromo]);

    // If subscriber has logged in via FastLogin then fetch their available promos
    useEffect(() => {
        if (isLoggedIn === true && isReadytoCheckout && totalTicketQuantity > 0) {
            (async () => {
                await handleFastLogIn();
            })();
        }
    }, [isLoggedIn, isReadytoCheckout]);

    // If subscriber has logged in via fast log in and selected tickets, then trigger checkout of basket
    useEffect(() => {
        if (hasFetchedPromo && totalTicketQuantity > 0) {
            checkoutBookingSession(totalTicketQuantity, "session");
        }
    }, [hasFetchedPromo, totalTicketQuantity]);

    // Checks if new ticket selection exceeds available redemption limit for active promo
    useEffect(() => {
        setError("");
        setPromoCodeMessage("");
    }, [selectedItems]);

    // On selection of items checks if value is greater than $0 then toggles state to show payment/complimentary checkout
    useEffect(() => {
        const totalValue = selectedItems.reduce((total, item) => {
            const itemValue = item.price_cents * item.quantity;
            return total + itemValue;
        }, 0);
        setShowCheckout(totalValue - (appliedPromo?.discount || 0) > 0);
    }, [appliedPromo, selectedItems]);

    const handleApplyPromo = () => {
        setReferralCode(referralCode);
    };

    const handleFastLogIn = async () => {
        if (hasFastLoggedIn) return;
        setHasFastLoggedIn(true);
        setHasFetchedPromo(false);
        try {
            await fetchApplyPromoAvailability();
            handleApplyPromo();
        } catch {
            setHasFastLoggedIn(false);
            return;
        }
        setHasFetchedPromo(true);
    };

    const purchasableItemKey = (item) => {
        return item.id && item.ticket_type_id
            ? `ticket-${item.id}-${item.ticket_type_id}`
            : `candy-bar-${item.bucket_id}`;
    };

    const handleChangeItemQuantity = (idKey, quantity, isSession) => {
        setCheckout(false);
        setError("");
        setAppliedPromo(null);

        if (!isSession && quantity > 10) {
            const existingItem = selectedItems.find(item => item.key === idKey);
            if (existingItem && quantity >= existingItem.quantity) {
                setError("The maximum quantity of candy bars you can purchase is 10");
                return;
            }
        }

        const type = isSession ? "ticket" : "candy-bar";

        setSelectedItems(currentItems => {
            const newSelection = [...currentItems];
            const index = newSelection.findIndex((item) => item.key === idKey);

            if (index === -1) {
                const choice = purchasable.find(
                    (item) => idKey === purchasableItemKey(item),
                );

                if (!choice) {
                    return currentItems;
                }

                if (isSession) {
                    newSelection.push({
                        key: idKey,
                        type,
                        item_type_id: choice.item_type_id,
                        ticket_type_id: choice.ticket_type_id,
                        promo: false,
                        session_id: choice.id,
                        session_inventory_id: choice.session_inventory_id,
                        price_cents: choice.ticket_price_cents,
                        rrp_pricing_cents: choice.rrp_pricing_cents,
                        saving: choice.saving,
                        quantity: 1,
                        ticket_class_id: choice.ticket_class_id,
                    });
                } else {
                    newSelection.push({
                        key: idKey,
                        type,
                        item_type_id: choice.item_type_id,
                        ticket_type_id: choice.ticket_type_id,
                        promo: false,
                        bucket_id: choice.bucket_id,
                        candy_bar: choice.candy_bar,
                        candy_bar_description: choice.candy_bar_description,
                        price_cents: choice.candy_bar_price,
                        candy_bar_rrp_price: choice.candy_bar_rrp_price,
                        rrp_pricing_cents: choice.candy_bar_rrp_price,
                        rebate_cents: choice.rebate_cents,
                        saving: choice.saving,
                        quantity: 1,
                    });
                }
            } else {
                newSelection[index].quantity = quantity;
            }

            return newSelection;
        });

        if (isSession) {
            setTicketQuantity(quantity);
        }
    };


    if (isLoadingSession || !session) {
        return <Loader isLoading={isLoadingSession} />;
    }

    const paidTicketQuantity =
        totalTicketQuantity >= appliedPromo?.num_free_tickets
            ? totalTicketQuantity - appliedPromo?.num_free_tickets
            : totalTicketQuantity;


    const hasCandyBarItems = selectedItems.some(item => item.type === "candy-bar" && item.quantity > 0);

    const handleClose = () => {
        const from = location.state?.from;
        if (from) {
            navigate(from);
            return;
        }
        if (session?.movie_id && session?.movie_title) {
            const movieSlug = session.movie_title
                .toLowerCase()
                .replace(/\s+/g, "-")
                .replace(/[^a-z0-9-]/g, "");
            navigate(`/movie/${session.movie_id}/${movieSlug}`);
        } else {
            navigate("/");
        }
    };


    return (
        <>
            <BookingHeartbeat />

            <div className="v3-booking__btn">
                <button className="v3-booking__close-btn" onClick={handleClose}><CloseIcon className="drawer__svg" /></button>
            </div>

            <div className="v3-booking__container">
                <PaymentStatus />
                <div className="v3-booking__info">
                    <BookSessionDetails />

                    {isFullyLoggedIn ? (
                        <RedemptionDetailsBase ticketClass={session?.ticket_class_id} />
                    ) : null}

                    <BookSessionInventory
                        handleChangeItemQuantity={handleChangeItemQuantity}
                    />
                </div>

                <div className="v3-booking__checkout">
                    {isSubmitting && <Loader isLoading={isSubmitting} />}
                    {isConfirmingPayment && (
                        <Loader isLoading={true} text="Confirming your payment, please wait..." />
                    )}
                    {paidTicketQuantity === 0 &&
                        hasCandyBarItems === false &&
                        showCheckout === false &&
                        isLoggedIn &&
                        isFullyLoggedIn &&
                        whiteLabel?.enable_agentic_ai !== true && (
                            <V3ComplimentaryCheckoutDrawer
                                uuid={uuid}
                                bookingSessionId={bookingSessionId}
                                session={session}
                            />
                        )
                    }
                </div>
            </div>
            {isLoggedIn && !drawerState.complimentaryCheckoutDrawer && <TermsDrawer session={session}/>}
            <StripeCheckoutDrawer />
        </>
    );
};

export default V2V3BookSession;
